import { useEffect, useRef, useState } from 'react';
import { api } from '../api.ts';
import { Dialog, Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';
import { ZuordnenKnopf } from './AuftragSuche.tsx';

/**
 * EINE NEUE MAIL (.285).
 *
 * ORIGINALTEXT MARVIN (02.10.2026)
 *   „ich kann da überhaupt keine emails raus senden"
 *
 * Es gab den Weg, aber er lag im zweiten Reiter hinter einem Postfach, das
 * erst gewählt sein wollte. Jetzt steht „Neue Mail" oben links im Postfach,
 * mit Vorschlägen beim Empfänger, Kopie, Vorlage und Auftrag. Der Versand
 * läuft durch denselben Weg wie jede Mail des Hauses (Testmodus, Riegel für
 * Behördenpost, Protokoll), danach steht sie als eigener Vorgang da.
 */

/** Ein Feld für Adressen, mit Vorschlägen für die gerade getippte. */
export function Adressfeld(
  { name, wert, setze, platzhalter, autoFocus }: {
    name: string; wert: string; setze: (w: string) => void; platzhalter?: string; autoFocus?: boolean;
  }
) {
  const [vorschlag, setVorschlag] = useState<any[]>([]);
  const nr = useRef(0);
  const teil = (wert.split(/[,;]/).pop() ?? '').trim();
  useEffect(() => {
    if (teil.length < 2) { setVorschlag([]); return; }
    const n = ++nr.current;
    const t = setTimeout(() => {
      api.postfachAdressen(teil).then((r) => { if (n === nr.current) setVorschlag(r.adressen ?? []); })
        .catch(() => setVorschlag([]));
    }, 200);
    return () => clearTimeout(t);
  }, [teil]);
  function nimm(a: string) {
    const davor = wert.split(/[,;]/).slice(0, -1).map((x) => x.trim()).filter(Boolean);
    setze([...davor, a].join(', ') + ', ');
    setVorschlag([]);
  }
  return (
    <div className="adressfeld">
      <input name={name} type="text" autoComplete="off" value={wert} autoFocus={autoFocus}
             placeholder={platzhalter} onChange={(e) => setze(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Escape') setVorschlag([]); }} />
      {vorschlag.length > 0 && (
        <div className="adressvorschlag">
          {vorschlag.map((v) => (
            <button key={v.adresse} type="button" onClick={() => nimm(v.adresse)}>
              <b>{v.name || v.adresse.split('@')[0]}</b> <span className="leise">{v.adresse}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Die Vorlagen des Hauses (AAZ, VAO, FAZ ...), nur die gefüllten. */
export function useVorlagen(): any[] {
  const [v, setV] = useState<any[]>([]);
  useEffect(() => {
    api.mailfachVorlagen().then((r) => setV((r.vorlagen ?? []).filter((x: any) => x.gefuellt))).catch(() => setV([]));
  }, []);
  return v;
}

export const adressen = (w: string) => w.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);

export default function MailSchreiben(
  { zu, fertig, an: anVor, auftrag }: {
    zu: () => void;
    fertig: (faden: string, satz: string) => void;
    an?: string;
    auftrag?: { id: string; text: string } | null;
  }
) {
  const [an, setAn] = useState(anVor ? anVor + ', ' : '');
  const [cc, setCc] = useState('');
  const [mitCc, setMitCc] = useState(false);
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [auftragWahl, setAuftragWahl] = useState<{ id: string; text: string } | null>(auftrag ?? null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const vorlagen = useVorlagen();

  async function senden() {
    setLaeuft(true); setFehler(null);
    try {
      const r = await api.postfachNeu({
        an: adressen(an), cc: adressen(cc), betreff, text, auftrag_id: auftragWahl?.id ?? null,
      });
      fertig(r.faden, r.gesendet
        ? (r.umgeleitet ? 'Gesendet, im Testmodus an die Testadresse.' : 'Die Mail ist unterwegs.')
        : `Nicht hinausgegangen: ${r.grund ?? 'unbekannt'}`);
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Neue Mail" breite={760} zu={zu} laeuft={laeuft}>
      <div className="mailformular"
           onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); senden(); } }}>
        <Fehlerkasten lage={fehler} />
        <label className="mailformzeile"><span>An</span>
          <Adressfeld name="an" wert={an} setze={setAn} platzhalter="Name oder Adresse" autoFocus={!anVor} />
          {!mitCc && <button type="button" className="knopf klein" onClick={() => setMitCc(true)}>Cc</button>}
        </label>
        {mitCc && (
          <label className="mailformzeile"><span>Cc</span>
            <Adressfeld name="cc" wert={cc} setze={setCc} />
          </label>
        )}
        <label className="mailformzeile"><span>Betreff</span>
          <input type="text" value={betreff} onChange={(e) => setBetreff(e.target.value)} autoFocus={!!anVor} />
        </label>
        <div className="mailformzeile"><span>Auftrag</span>
          <div className="wachsen">
            {auftragWahl ? (
              <span className="reihe">
                <span className="merker">{auftragWahl.text}</span>
                <button type="button" className="knopf klein" onClick={() => setAuftragWahl(null)}>weg</button>
              </span>
            ) : (
              <ZuordnenKnopf laeuft={laeuft} ohneAufheben titel="Auftrag wählen (freiwillig)"
                             tu={(id, t) => setAuftragWahl(id ? { id, text: t ?? 'Auftrag' } : null)} />
            )}
          </div>
        </div>
        {vorlagen.length > 0 && (
          <div className="mailformzeile"><span>Vorlage</span>
            <select value="" onChange={(e) => {
              const v = vorlagen.find((x) => x.wert === e.target.value);
              if (!v) return;
              if (!betreff) setBetreff(v.betreff);
              setText((t) => (t ? t + '\n\n' : '') + v.text);
            }}>
              <option value="">einfügen …</option>
              {vorlagen.map((v) => <option key={v.wert} value={v.wert}>{v.name}</option>)}
            </select>
          </div>
        )}
        <textarea className="feld mailtextfeld" rows={14} value={text} placeholder="Text"
                  onChange={(e) => setText(e.target.value)} />
        <p className="klein leise" style={{ margin: 0 }}>
          Platzhalter einer Vorlage wie {'{Adresse}'} bitte von Hand ersetzen. Im Testmodus geht die Mail an
          die Testadresse. Die Signatur setzt der Versand selbst.
        </p>
        <div className="reihe">
          <button type="button" className="knopf haupt" disabled={laeuft || !adressen(an).length || !betreff.trim() || !text.trim()}
                  onClick={senden}>{laeuft ? 'Wird gesendet' : 'Senden'}</button>
          <button type="button" className="knopf" onClick={zu} disabled={laeuft}>Verwerfen</button>
          <span className="klein leise">Strg + Enter sendet</span>
        </div>
      </div>
    </Dialog>
  );
}
